import { useState } from "react";
import { Alert, Box, Card, CardContent, Link, Stack, Typography } from "@mui/material";
import { GoogleLogin, type CredentialResponse } from "@react-oauth/google";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { GOOGLE_HOSTED_DOMAIN } from "@/config";

export function LoginPage() {
  const { user, loginWithGoogle } = useAuth();
  const [error, setError] = useState<string | null>(null);
  // Staff get Google's account picker narrowed to the company domain. Invited
  // outside accounts (e.g. an accounting firm) switch to an unrestricted picker;
  // the server still only lets in allowlisted, invited addresses.
  const [external, setExternal] = useState(false);

  if (user) return <Navigate to="/" replace />;

  async function onSuccess(resp: CredentialResponse) {
    try {
      setError(null);
      await loginWithGoogle(resp.credential ?? "");
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? "Sign-in failed.");
    }
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 12 }}>
      <Card sx={{ maxWidth: 420, width: "100%" }}>
        <CardContent>
          <Stack spacing={2} alignItems="center">
            <Box component="img" src="/logo.png" alt="Freedom Power" sx={{ height: 44, mb: 1 }} />
            <Typography variant="h5">FP Accounting</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {external
                ? "Sign in with the Google account for the email address you were invited with."
                : "Sign in with your Freedom Power Google account."}
            </Typography>
            {error && <Alert severity="error" sx={{ width: "100%" }}>{error}</Alert>}
            <GoogleLogin
              key={external ? "external" : "staff"}
              hosted_domain={external ? undefined : GOOGLE_HOSTED_DOMAIN || undefined}
              onSuccess={onSuccess}
              onError={() => setError("Google sign-in failed.")}
            />
            <Link component="button" type="button" variant="body2" onClick={() => { setError(null); setExternal(!external); }}>
              {external ? "Back to Freedom Power sign-in" : "Signing in with a non-Freedom Power Google account?"}
            </Link>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
